/**
 * Utility functions to prevent 22P02 bigint errors
 * by routing queries to the correct filter (id vs mint_address)
 */

/**
 * Check if a value is a valid numeric piece ID
 * @param {any} value - Value to check
 * @returns {number|null} - Numeric ID or null
 */
export function asNumericId(value) {
  // Direct number check
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  
  // String check - only parse if it's pure digits
  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Only accept strings that are purely numeric (no base58 chars)
    if (/^\d+$/.test(trimmed)) {
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }
  }
  
  return null;
}

/**
 * Check if a value looks like a Solana mint address
 * @param {any} value - Value to check
 * @returns {boolean}
 */
export function isMintAddress(value) {
  if (typeof value !== 'string' || !value) return false;
  
  const trimmed = value.trim();
  
  // Solana addresses are base58 strings, typically 32-64 characters
  if (trimmed.length < 32 || trimmed.length > 64) return false;
  
  // No spaces allowed
  if (trimmed.includes(' ')) return false;
  
  // Basic base58 character check (no 0, O, I, l)
  const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
  return base58Regex.test(trimmed);
}

/**
 * Build a Supabase query filter using either id or mint_address
 * @param {Object} query - Supabase query builder
 * @param {Object} params - Filter parameters
 * @param {number|string} [params.pieceId] - Numeric piece ID or mint address string
 * @param {string} [params.mintAddress] - Mint address
 * @param {boolean} [params.isWrite] - True if this is a write operation (throws on invalid input)
 * @returns {Object} - Query with filter applied
 * @throws {Error} - If isWrite=true and no valid identifier provided
 */
export function applyPieceFilter(query, { pieceId, mintAddress, isWrite = false }) {
  const numericId = asNumericId(pieceId);
  
  if (numericId !== null) {
    // Use numeric ID filter
    return query.eq('id', numericId);
  }
  
  // Check if pieceId is actually a mint address string (coercion)
  if (typeof pieceId === 'string' && isMintAddress(pieceId)) {
    console.log('🔄 Coerced pieceId string to mint_address:', pieceId.slice(0, 8) + '...');
    return query.eq('mint_address', pieceId.trim());
  }
  
  // Check explicit mintAddress param
  if (isMintAddress(mintAddress)) {
    return query.eq('mint_address', mintAddress.trim());
  }
  
  // No valid filter
  if (isWrite) {
    throw new Error('Invalid piece identifier: must provide numeric pieceId or valid mintAddress');
  }
  
  // For reads, return impossible filter (no results, no error)
  console.warn('applyPieceFilter: No valid pieceId or mintAddress provided', { pieceId, mintAddress });
  return query.eq('id', -1); // Non-existent ID
}

/**
 * Build a Supabase query filter for listings
 * @param {Object} query - Supabase query builder
 * @param {Object} params - Filter parameters
 * @param {number} [params.listingId] - Numeric listing ID
 * @param {string} [params.mintAddress] - Mint address
 * @returns {Object} - Query with filter applied
 */
export function applyListingFilter(query, { listingId, mintAddress }) {
  const numericId = asNumericId(listingId);
  
  if (numericId !== null) {
    return query.eq('id', numericId);
  } else if (isMintAddress(mintAddress)) {
    return query.eq('mint_address', mintAddress.trim());
  } else {
    console.warn('applyListingFilter: No valid listingId or mintAddress provided', { listingId, mintAddress });
    return query.eq('id', -1);
  }
}

/**
 * Fetch piece ID by mint address
 * Useful when downstream functions require a numeric ID
 * @param {Object} supabase - Supabase client
 * @param {string} mintAddress - Mint address
 * @returns {Promise<number|null>} - Numeric piece ID or null
 */
export async function getPieceIdByMint(supabase, mintAddress) {
  if (!isMintAddress(mintAddress)) {
    console.warn('getPieceIdByMint: Invalid mint address', mintAddress);
    return null;
  }
  
  try {
    const { data, error } = await supabase
      .from('pieces')
      .select('id')
      .eq('mint_address', mintAddress.trim())
      .maybeSingle();
    
    if (error || !data) {
      console.warn('getPieceIdByMint: No piece found for mint', mintAddress);
      return null;
    }
    
    return data.id;
  } catch (error) {
    console.error('getPieceIdByMint: Error fetching piece ID', error);
    return null;
  }
}

/**
 * Validate and normalize piece identifiers with coercion
 * @param {Object} params - Parameters
 * @param {any} [params.pieceId] - Piece ID (might be string or number)
 * @param {any} [params.mintAddress] - Mint address
 * @returns {Object} - Normalized identifiers { pieceId: number|null, mintAddress: string|null }
 */
export function normalizePieceIdentifiers({ pieceId, mintAddress }) {
  const numericId = asNumericId(pieceId);
  
  // If pieceId is numeric, use it
  if (numericId !== null) {
    return {
      pieceId: numericId,
      mintAddress: isMintAddress(mintAddress) ? mintAddress.trim() : null,
    };
  }
  
  // Coerce: if pieceId is a base58 string, treat it as mintAddress
  if (typeof pieceId === 'string' && isMintAddress(pieceId)) {
    return {
      pieceId: null,
      mintAddress: pieceId.trim(),
    };
  }
  
  // Use explicit mintAddress if provided
  return {
    pieceId: null,
    mintAddress: isMintAddress(mintAddress) ? mintAddress.trim() : null,
  };
}

/**
 * Create friendly error message for missing piece
 * @param {Object} params - Parameters
 * @param {number} [params.pieceId] - Piece ID
 * @param {string} [params.mintAddress] - Mint address
 * @returns {string} - Error message
 */
export function createPieceNotFoundError({ pieceId, mintAddress }) {
  const parts = [];
  
  if (pieceId) parts.push(`id=${pieceId}`);
  if (mintAddress) parts.push(`mint=${mintAddress.slice(0, 8)}...`);
  
  if (parts.length === 0) {
    return 'Piece not found: no valid identifier provided';
  }
  
  return `Piece not found: ${parts.join(', ')}`;
}

